import {Role} from "src/app/constant/Enums";

/**
 * UI capabilities per role for the Case Audit (Management) page.
 *
 * Authoritative source: Case Verifier HLD §2.1 (User Stories) and §6.8 (Search API
 * Role-Based Scope); Case Verifier LLD §9.5. Summary:
 *   - Auditors (Primary/Secondary): own completed cases only, alias locked, read-only (View).
 *   - QA: all cases globally, optional alias filter, Reopen "No Action" cases.
 *   - Manager: all cases globally, optional alias filter, Bulk No-Action, export case data,
 *     export performance stats.
 *   - UNKNOWN (HLD §6.4): no capabilities.
 *
 * NOTE 1: This gates the UI only. The backend independently enforces every privileged
 * operation from the Midway session; these flags must never be treated as security.
 * NOTE 2: Per-row actions (e.g. Re-open) are ultimately driven by the `availableActions`
 * array the Search API returns per row (HLD §6.8 / LLD §9.3). `canReopen` here is a
 * client-side convenience guard, not the source of truth.
 */
export interface RoleCapabilities {
    /** Can filter by an arbitrary auditor alias (Search Login). Auditors are locked to self. */
    canSearchByLogin: boolean;
    /** Sees all cases globally vs only their own. */
    scopeAllCases: boolean;
    /** Can reopen eligible (NO_ACTION + COMPLETED) cases. */
    canReopen: boolean;
    /** Can bulk no-action selected PENDING cases. */
    canBulkNoAction: boolean;
    /** Can export the case list as CSV. */
    canExportCases: boolean;
    /** Can export per-auditor performance metrics. */
    canExportPerformance: boolean;
}

const AUDITOR_CAPS: RoleCapabilities = {
    canSearchByLogin: false,
    scopeAllCases: false,
    canReopen: false,
    canBulkNoAction: false,
    canExportCases: false,
    canExportPerformance: false
};

export const ROLE_CAPABILITIES: Record<Role, RoleCapabilities> = {
    [Role.PRIMARY_AUDITOR]: AUDITOR_CAPS,
    [Role.SECONDARY_AUDITOR]: AUDITOR_CAPS,
    [Role.UNKNOWN]: AUDITOR_CAPS,
    [Role.QA]: {
        canSearchByLogin: true,
        scopeAllCases: true,
        canReopen: true,
        canBulkNoAction: false,
        canExportCases: false,
        canExportPerformance: false
    },
    [Role.MANAGER]: {
        canSearchByLogin: true,
        scopeAllCases: true,
        canReopen: true,
        canBulkNoAction: true,
        canExportCases: true,
        canExportPerformance: true
    }
};

export function capabilitiesFor(role: Role): RoleCapabilities {
    return ROLE_CAPABILITIES[role] || AUDITOR_CAPS;
}
